import React from 'react';
import { Card } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfDay } from 'date-fns';
import { Sparkles, TrendingUp } from 'lucide-react';

export default function LifeExpansionCalendar() {
  const { data: checkIns, isLoading } = useQuery({
    queryKey: ['life-expansion-calendar'],
    queryFn: async () => {
      const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const { data, error } = await supabase
        .from('daily_checkins')
        .select('date, life_expansion_completed, life_expansion_difficulty, life_expansion_activity_category')
        .gte('date', thirtyDaysAgo)
        .order('date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  const getLast30Days = () => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
      days.push(format(subDays(startOfDay(new Date()), i), 'yyyy-MM-dd'));
    }
    return days;
  };

  const getActivityColor = (date: string) => {
    const checkIn = checkIns?.find(c => c.date === date);
    if (!checkIn?.life_expansion_completed) return 'bg-muted';
    
    switch (checkIn.life_expansion_difficulty) {
      case 'easy':
        return 'bg-primary/80';
      case 'medium':
        return 'bg-primary/60';
      case 'hard':
        return 'bg-primary/40';
      default:
        return 'bg-primary/60';
    }
  };

  const completedCount = checkIns?.filter(c => c.life_expansion_completed).length || 0;
  const currentStreak = calculateStreak();

  function calculateStreak(): number {
    if (!checkIns || checkIns.length === 0) return 0;
    
    let streak = 0;
    const sortedCheckIns = [...checkIns].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    for (const checkIn of sortedCheckIns) {
      if (checkIn.life_expansion_completed) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 30 }).map((_, i) => (
              <div key={i} className="aspect-square bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0" />
            <span className="truncate">Life Expansion Journey</span>
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-1">
            Your 30-day behavioural activation progress
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl sm:text-2xl font-bold text-primary">{completedCount}</div>
          <div className="text-[10px] sm:text-xs text-muted-foreground">activities completed</div>
        </div>
      </div>

      {currentStreak > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 bg-primary/10 rounded-lg">
          <TrendingUp className="w-5 h-5 text-primary" />
          <div>
            <div className="font-semibold text-primary">{currentStreak} Day Streak!</div>
            <div className="text-xs text-muted-foreground">Keep expanding your comfort zone</div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-7 gap-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-xs text-center text-muted-foreground font-medium">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {getLast30Days().map((date) => {
            const checkIn = checkIns?.find(c => c.date === date);
            return (
              <div
                key={date}
                className={`aspect-square rounded-md transition-all hover:scale-110 ${getActivityColor(date)}`}
                title={checkIn?.life_expansion_completed 
                  ? `${format(new Date(date), 'MMM d')}: ${checkIn.life_expansion_activity_category || 'Completed'} (${checkIn.life_expansion_difficulty})` 
                  : format(new Date(date), 'MMM d')}
              />
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary/80"></div>
            <span>Easy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary/60"></div>
            <span>Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary/40"></div>
            <span>Hard</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
